import {
  Line3,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Path,
  Plane,
  Shape,
  ShapeGeometry,
  Sphere,
  Vector3
} from "three";
import { CameraHandler, CameraImage } from ".";
import { MarkerNavType } from "../../redux/settings-slice";
import { CustomMouseEvent } from "../controls";
import { RayCaster } from "../ray-caster";
import { toVector2 } from "../utilities";
import { calculateEigenVectors } from "../projections";

interface IntersectResponse {
  camera: CameraImage;
  intersect: Vector3;
  rotation: number;
}

export class SceneViewCameras {
  public cameraHandler: CameraHandler;
  private arrowNavigation: ArrowNavigation;
  public markerNavigation: MarkerNavigation;
  private modifier = false;
  private navigationType: MarkerNavType;

  constructor(cameraHandler: CameraHandler) {
    this.cameraHandler = cameraHandler;
    this.markerNavigation = new MarkerNavigation(this);
    this.arrowNavigation = new ArrowNavigation(this);

    this.initEvents();
  }

  get viewer() {
    return this.cameraHandler.viewer;
  }

  get files() {
    return this.cameraHandler.files;
  }

  get scene() {
    return this.cameraHandler.markerScene;
  }

  get renderInFront() {
    return this.viewer.orbitState || this.arrowNavigation.visible;
  }

  get markersVisible() {
    return this.markerNavigation.visible;
  }

  initEvents() {
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    window.addEventListener( 'keydown', this.onKeyDown, false);
    window.addEventListener( 'keyup', this.onKeyUp, false);
  }

  removeEvents() {
    window.removeEventListener('keydown', this.onKeyDown, false);
    window.removeEventListener('keyup', this.onKeyUp, false);
  }

  onKeyUp(event) {
    if (event.key === "Control") {
      this.modifier = false;
    }
  }

  onKeyDown(event) {
    if (event.key === "Control") {
      this.modifier = true;
    }
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    if (event.mouseMoved) return false;
    if (!event.isLeftClick) return false;

    const camera = this.markerNavigation.onMouseUp(event)
      || this.arrowNavigation.onMouseUp(event);

    if (camera) {
      this.viewer.loadImage(camera.id);
      return true;
    }

    return false;
  }

  onMouseMove(event: CustomMouseEvent) {
    if (event.mouseMoved) return;
    this.arrowNavigation.onMouseMove(event);
    this.markerNavigation.onMouseMove(event);
  }

  setNavigationType(type: MarkerNavType) {
    this.navigationType = type;
    this.markerNavigation.updatePicker();
    this.clearArrowNavigation();
  }

  updateArrowNavigation() {
    this.arrowNavigation.updateNavigation();
  }

  clearArrowNavigation() {
    this.arrowNavigation.clearNavigation();
  }

  updateVisibility() {
    const isOrbitMode = this.viewer.orbitState;
    const isHidden = this.navigationType === MarkerNavType.None;
    const isArrowMode = this.navigationType === MarkerNavType.Arrows;
    const isMarkerMode = this.navigationType === MarkerNavType.Markers;

    if (isHidden) {
      this.markerNavigation.setVisible(false);
      this.arrowNavigation.setVisible(false);
    } else if (isMarkerMode || isOrbitMode || this.modifier) {
      this.markerNavigation.setVisible(true);
      this.arrowNavigation.setVisible(false);
    } else if (isArrowMode) {
      this.markerNavigation.setVisible(false);
      this.arrowNavigation.setVisible(true);
    }

    this.markerNavigation.setEnabled(isArrowMode || isMarkerMode);
    this.arrowNavigation.setEnabled(isArrowMode);
  }

  update() {
    this.updateVisibility();
    this.files.forEach(file => file.update());
    this.arrowNavigation.update();
  }
}

class MarkerNavigation {
  private sceneViewController: SceneViewCameras;
  private selected: CameraImage;
  public enabled = false;
  public visible = false;

  constructor(sceneViewCameras: SceneViewCameras) {
    this.sceneViewController = sceneViewCameras;
  }

  get files() {
    return this.sceneViewController.files;
  }

  get viewer() {
    return this.sceneViewController.viewer;
  }

  get picker() {
    return this.viewer.gpuPickers.markers;
  }

  get cameraHeight() {
    const handler = this.sceneViewController.cameraHandler;
    return handler.cameraHeight;
  }

  updatePicker() {
    this.picker.needsUpdate = true;
  }

  getPickerResults(event: CustomMouseEvent): CameraImage {
    try {
      this.picker.update();

      const pickerID = this.picker.value(event);
      if (pickerID === null) return null;

      for (let file of this.files) {
        const camera = file.getByPickerID(pickerID);
        if (camera) return camera;
      }
    } catch (e) {
      console.log("Prevented framebuffer exception: ");
      console.log(e);
      return null;
    }
  }

  /** @private */
  markCameraSelected(camera: CameraImage) {
    for (let file of this.files) {
      file.setSelected(camera);
    }

    if (this.selected !== camera) {
      this.updatePicker();
    }

    this.selected = camera;
  }

  onMouseUp(event: CustomMouseEvent): CameraImage {
    if (!this.visible) return;

    return this.getPickerResults(event);
  }

  onMouseMove(event: CustomMouseEvent) {
    if (!this.visible) return;

    const camera = this.getPickerResults(event);
    this.markCameraSelected(camera);
  }

  setEnabled(state: boolean) {
    this.enabled = state;
  }

  setVisible(state: boolean) {
    if (this.visible === state) return;

    this.visible = state;
    this.updatePicker();
  }
}

class ArrowNavigation {
  private sceneViewController: SceneViewCameras;
  private nearbyCameras: CameraImage[] = [];
  private cameraPlane: Plane = null;

  private cameraObject: Mesh;
  private cursorObject: Mesh;

  public enabled = false;
  public visible = false;
  private needsUpdate = false;
  private calculating = false;
  private minCameraDistance = 1.0;
  private maxCameraDistance = 40.0;

  constructor(sceneViewCameras: SceneViewCameras) {
    this.sceneViewController = sceneViewCameras;

    this.createCameraObject();
    this.createCursorObject();
    this.setObjectsVisible(false);
    this.clearNavigation();
  }

  get files() {
    return this.sceneViewController.files;
  }

  get viewer() {
    return this.sceneViewController.viewer;
  }

  get scene() {
    return this.sceneViewController.scene;
  }

  get cameraHeight() {
    return this.sceneViewController.cameraHandler.cameraHeight;
  }

  /** @private */
  createCameraObject() {
    const shape = new Shape();
    shape.moveTo( 0.60, 0.60 );
    shape.lineTo( 1.00, 0.60 );
    shape.lineTo( 1.00, 0.40 );
    shape.lineTo( 0.60, 0.40 );
    shape.lineTo( 0.60, 0.00 );
    shape.lineTo( 0.40, 0.00 );
    shape.lineTo( 0.40, 0.40 );
    shape.lineTo( 0.00, 0.40 );
    shape.lineTo( 0.00, 0.60 );
    shape.lineTo( 0.40, 0.60 );
    shape.lineTo( 0.40, 1.00 );
    shape.lineTo( 0.60, 1.00 );
    shape.lineTo( 0.60, 0.60 );

    const geometry = new ShapeGeometry(shape);
    const material = new MeshBasicMaterial({
      color: 0xFFFFFF,
      opacity: 0.8,
      transparent: true
    });

    this.cameraObject = new Mesh(geometry,  material);
    this.cameraObject.geometry.center();
    this.scene.add(this.cameraObject);
  }

  /** @private */
  createCursorObject() {
    const shape = new Shape();
    shape.moveTo( 0.00, 1.00 );
    shape.absarc( 0.50, 0.50, 0.50, 0, Math.PI * 2, false );

    const hole = new Path();
    hole.moveTo( 0.15, 0.70 );
    hole.lineTo( 0.50, 0.70 );
    hole.lineTo( 0.50, 0.80 );
    hole.lineTo( 0.85, 0.51 );
    hole.lineTo( 0.85, 0.49 );
    hole.lineTo( 0.50, 0.20 );
    hole.lineTo( 0.50, 0.30 );
    hole.lineTo( 0.15, 0.30 );
    hole.lineTo( 0.15, 0.70 );

    shape.holes.push(hole);

    const geometry = new ShapeGeometry(shape);
    const material = new MeshBasicMaterial({
      color: 0xEAEAEA
    });

    this.cursorObject = new Mesh(geometry,  material);
    this.cursorObject.geometry.center();
    this.scene.add(this.cursorObject);
  }

  onMouseMove(event) {
    if (!this.visible) return;

    const {camera, intersect, rotation} = this.getBestCamera(event);
    if (!camera) {
      this.setObjectsVisible(false);
      return;
    }

    this.setObjectsVisible(true);

    const position = camera.position.scene;
    const cameraPosition = new Vector3(
      position.x,
      position.y,
      position.z - this.cameraHeight
    );

    if (intersect) {
      this.cursorObject.position.copy(intersect);
      this.cursorObject.rotation.z = rotation;
    } else {
      this.cursorObject.visible = false;
    }

    this.cameraObject.position.copy(cameraPosition);
    this.cameraObject.position.z -= 0.25;
    this.cameraObject.rotation.z = rotation + MathUtils.degToRad(45);
  }

  onMouseUp(event) {
    if (!this.visible) return;

    const {camera} = this.getBestCamera(event);
    return camera;
  }

  getBestCamera(event: CustomMouseEvent): IntersectResponse {
    const defaultResponse = {
      camera: null,
      intersect: null,
      rotation: 0
    };

    return this.getCameraFromPlane(event)
      || this.getCameraFromDirection(event)
      || defaultResponse;
  }

  /** @private */
  getCameraFromDirection(event: CustomMouseEvent): IntersectResponse {
    let closestCamera: CameraImage;

    const raycaster = new RayCaster(this.viewer, event);

    const start = raycaster.position;
    const end = new Vector3()
      .copy(raycaster.position)
      .add(raycaster.direction);

    const line = new Line3(start, end);
    const nextCamera = this.viewer.getNextCamera();
    const previousCamera = this.viewer.getPreviousCamera();
    if (!nextCamera || !previousCamera) return;

    const P0 = line.closestPointToPointParameter(
      nextCamera.position.scene);

    const P1 = line.closestPointToPointParameter(
      previousCamera.position.scene);

    if (P0 > 0) {
      closestCamera = nextCamera;
    } else if (P1 > 0) {
      closestCamera = previousCamera;
    } else {
      return;
    }

    const rotation = getRotation(closestCamera, raycaster);

    return {
      camera: closestCamera,
      intersect: null,
      rotation: rotation
    };
  }

  /** @private */
  getCameraFromPlane(event: CustomMouseEvent): IntersectResponse {
    if (this.nearbyCameras.length === 0) return;

    const raycaster = new RayCaster(this.viewer, event);
    const intersection = raycaster.intersectPlane(this.cameraPlane);
    const frustum = this.viewer.getCameraFrustum();
    if (!intersection) return;

    const intersect2D = toVector2(intersection);

    let closestCamera: CameraImage = null;
    let closestDistance: number = Infinity;

    // Determine which cameras are visible in the view frustum
    const visibleCameras = this.nearbyCameras.filter(camera => {
      const position = camera.position.scene;
      const spherePosition = new Vector3(
        position.x,
        position.y,
        position.z - this.cameraHeight
      );

      const sphere = new Sphere(spherePosition, 0.5);
      return frustum.intersectsSphere(sphere);
    });

    if (visibleCameras.length === 0) return;

    // Grab the camera closest to our intersection point
    for (let camera of visibleCameras) {
      const position = camera.position.scene;
      const position2D = toVector2(position);
      const distance = position2D.distanceTo(intersect2D);
      if (distance >= closestDistance) continue;

      closestDistance = distance;
      closestCamera = camera;
    }

    const rotation = getRotation(closestCamera, raycaster);

    return {
      camera: closestCamera,
      intersect: intersection,
      rotation: rotation
    };
  }

  setEnabled(state: boolean) {
    this.enabled = state;
  }

  setVisible(state:boolean) {
    if (this.visible === state) return;

    this.visible = state;
    this.clearNavigation();
  }

  setObjectsVisible(visible: boolean) {
    if (this.cursorObject) {
      this.cursorObject.visible = visible;
    }

    if (this.cameraObject) {
      this.cameraObject.visible = visible;
    }
  }

  updateNavigation() {
    this.needsUpdate = true;
    this.clearNavigation();
  }

  clearNavigation() {
    this.setObjectsVisible(false);
  }

  calculateClosestCameras() {
    this.calculating = true;

    this.calculateNearbyCameras();
    this.calculateCameraPlane();

    this.calculating = false;
    this.needsUpdate = false;
  }

  /** @private */
  calculateNearbyCameras() {
    const camera = this.viewer.camera;
    const cameraPosition = camera.position;

    this.nearbyCameras = [];

    for (let file of this.files) {
      if (!file.visible) continue;

      file.cameras.forEach(camera => {
        const position = camera.position.scene;

        const dX = position.x - cameraPosition.x;
        if (dX > this.maxCameraDistance) return;

        const dY = position.y - cameraPosition.y;
        if (dY > this.maxCameraDistance) return;

        const distance = Math.sqrt(dX*dX + dY*dY);

        // Calculate min and max distance from camera;
        let tooClose = distance < this.minCameraDistance;
        let tooFar = distance > this.maxCameraDistance;
        if (tooClose || tooFar) return;

        this.nearbyCameras.push(camera);
      });
    }
  }

  /** @private */
  calculateCameraPlane() {
    const camera = this.viewer.camera;
    const cameraPosition = camera.position;

    if (this.nearbyCameras.length === 0) return;

    const points = this.nearbyCameras.map(camera => {
      // A small offset will create a more consistent plane in certain
      // situations (ex: perfectly straight line of points)
      const dx = Math.random()*0.5;
      const dy = Math.random()*0.5;
      const offset = new Vector3(dx, dy, 0);

      return new Vector3()
        .copy(camera.position.scene)
        .add(offset);
    });

    const {eigen} = calculateEigenVectors(points);

    let normal = new Vector3();
    let minAngle = Infinity;

    // Use the eigenvector that is closest to [0,0,1]
    // (regardless if it is the smallest eigenvalue)
    eigen.forEach(values => {
      const {vector} = values;
      if (vector.z < 0) {
        vector.multiplyScalar(-1);
      }

      const vertical = new Vector3(0,0,1);
      const angle = vector.angleTo(vertical);
      if (angle > minAngle) return;

      minAngle = angle;
      normal = vector;
    });

    const height = Math.max(1.0, this.cameraHeight);
    const point = new Vector3()
      .copy(cameraPosition)
      .sub(new Vector3(0, 0, height));

    this.cameraPlane = new Plane()
      .setFromNormalAndCoplanarPoint(normal, point);
  }

  update() {
    if (this.needsUpdate && !this.calculating) {
      this.calculateClosestCameras();
    }
  }
}

const getRotation = (camera: CameraImage, raycaster: RayCaster) => {
  const position = new Vector3()
    .copy(camera.position.scene)
    .sub(raycaster.position);

  const position2D = toVector2(position);

  return position2D.angle();
};
